/**
 * What3WordsInput — a text field for a what3words address.
 *
 *   - Shows the "///" prefix so staff know what to type.
 *   - Validates the three-word shape as you type (no network).
 *   - Offers autosuggest (via the API proxy, when the platform has a key),
 *     clipped to the account's country — the same three common words exist
 *     all over the world, so the clip is what makes suggestions useful.
 *   - Links the current value to the what3words map for a sanity check.
 *
 * The parent owns the value; the input reports the raw text and the parent
 * normalises on save (or lets the API do it).
 */
import { what3wordsAutosuggest } from '@/lib/api';
import { cn } from '@/lib/utils';
import { isWhat3Words, normaliseWhat3Words, what3wordsMapUrl } from '@/lib/what3words';
import { ExternalLink } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

interface Props {
  value: string;
  onChange: (v: string) => void;
  /** ISO-3166 alpha-2 used to clip suggestions (e.g. the facility country). */
  country?: string | null;
  id?: string;
  className?: string;
  placeholder?: string;
  'aria-label'?: string;
  disabled?: boolean;
}

export function What3WordsInput({
  value,
  onChange,
  country = null,
  id,
  className,
  placeholder = 'filled.count.soap',
  'aria-label': ariaLabel,
  disabled = false,
}: Props) {
  const listId = useId();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const seq = useRef(0);

  const text = value.trim();
  const valid = text.length === 0 || isWhat3Words(text);
  const normalised = normaliseWhat3Words(text);

  // Autosuggest after three characters, debounced; silently no-ops when the
  // platform has no key (the API returns configured=false).
  useEffect(() => {
    const raw = text.replace(/^\/+/, '');
    if (raw.length < 3 || disabled) {
      setSuggestions([]);
      return;
    }
    const mine = ++seq.current;
    const handle = setTimeout(() => {
      what3wordsAutosuggest(raw, country)
        .then((r) => {
          if (mine === seq.current) setSuggestions(r.suggestions.map((s) => s.words));
        })
        .catch(() => {
          if (mine === seq.current) setSuggestions([]);
        });
    }, 250);
    return () => clearTimeout(handle);
  }, [text, country, disabled]);

  return (
    <div className={className}>
      <div
        className={cn(
          'flex h-9 items-center rounded-md border bg-background pl-3 text-sm focus-within:ring-2 focus-within:ring-ring/30',
          valid ? 'border-border' : 'border-destructive',
        )}
      >
        <span className="select-none font-medium text-destructive/80" aria-hidden="true">
          {'///'}
        </span>
        <input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={ariaLabel ?? 'what3words address'}
          aria-invalid={!valid}
          list={listId}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          className="num h-full w-full bg-transparent px-1 outline-none disabled:opacity-60"
        />
        {normalised && (
          <a
            href={what3wordsMapUrl(normalised)}
            target="_blank"
            rel="noreferrer"
            className="grid h-full w-8 shrink-0 place-items-center text-muted-foreground hover:text-foreground"
            aria-label="Open in what3words"
            title="Open in what3words"
          >
            <ExternalLink className="size-3.5" strokeWidth={1.75} />
          </a>
        )}
      </div>
      <datalist id={listId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      {!valid && (
        <p className="mt-1 text-xs text-destructive">
          A what3words address is three words separated by dots, e.g. filled.count.soap
        </p>
      )}
    </div>
  );
}
